import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from './components/Navbar';
import { API_BASE_URL } from '../config/ApiConfig';
import Pagination from './components/Pagination';
import ResponsiveShadow from './components/ResponsiveShadow';
import { formatString } from './utils/StringUtils';
import { formatCurrency, formatQuantidade } from './utils/formatMoeda';
import ExcelUploadModal from './components/modalServices/ExcelUploadModal';
import DfdFindItemModal from './components/modalServices/DfdFindItemModal';
import DefaultModal from './components/modals/DefaultModal';
import useDefaultModal from './components/hooks/useDefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import usePagination from './components/hooks/usePagination';
import Footer from './components/Footer';
import TableHeader from './components/TableHeader';

function PcaItens() {
  const [dfd, setDfd] = useState({
    cnpj: '',
    num: '',
    dataContratacao: '',
    requisitante: '',
    objeto: '',
    prioridade: '',
    justificativaPrioridade: '',
    justificativaDemanda: '',
    tipo: '',
    usuario: '',
  });
  const [dfdItens, setDfdItens] = useState([]);
  const [dfdList, setDfdList] = useState([]);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [showFindModal, setShowFindModal] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [categoriaItem, setCategoriaItem] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const token = localStorage.getItem('token');
  const location = useLocation();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const totalItems = dfdItens.length;
  const { setPagination } = usePagination(totalItems, 5);
  const { startIndex, rowsPerPage } = setPagination;
  const selectedDfdItens = dfdItens.slice(startIndex, startIndex + rowsPerPage);
  // On mount: set dfd from navigation state OR load available DFDs for cnpj/anoBase from localStorage
  useEffect(() => {
    if (location.state && location.state.dfd) {
      setDfd(location.state.dfd);
      return;
    }

    const storedCnpj = localStorage.getItem('cnpj');
    const anoBase = localStorage.getItem('anoBase') || new Date().getFullYear().toString();
    if (!storedCnpj) return;

    // Buscar lista de DFDs para o CNPJ/anoBase e permitir seleção caso haja mais de um
    axios
      .get(`${API_BASE_URL.DFD}/cnpj`, {
        params: { cnpj: storedCnpj, anoBase: anoBase },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const lista = result.data || [];
        if (lista.length === 1) {
          setDfd(lista[0]);
        } else {
          setDfdList(lista);
        }
      })
      .catch(error => {
        console.error('Erro ao obter DFDs por CNPJ/anoBase:', error);
      });
    // eslint-disable-next-line
  }, [location.state]);

  // Quando `dfd` estiver definido com `cnpj` e `num`, buscar os itens e dados auxiliares
  useEffect(() => {
    if (!dfd || !dfd.cnpj) return;

    const dfdNumParam = dfd.num || dfd.dfdNum;
    if (!dfdNumParam) return; // precisa do número do DFD para buscar itens

    axios
      .get(`${API_BASE_URL.DFD_ITEM}/demanda`, {
        params: { cnpj: dfd.cnpj, num: dfdNumParam },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const formattedData = result.data.map(item => ({
          ...item,
          vlrUnit: formatCurrency(item.vlrUnit),
        }));
        setDfdItens(formattedData);
      })
      .catch(error => {
        console.error('Erro ao obter listas:', error);
      });

    axios
      .get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => setUnidades(result.data))
      .catch(error => console.error('Erro ao obter unidades:', error));

    axios
      .get(`${API_BASE_URL.CATEGORIA_ITEM}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => setCategoriaItem(result.data))
      .catch(error => console.error('Erro ao obter categorias:', error));
  }, [dfd, shouldFetch]);

  if (!dfdItens) {
    return <div>Loading...</div>;
  }

  function handleDelete(dfdToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(dfdToDelete));
  }

  function handleDeleteConfirm(itemToDelete) {
    axios
      .delete(`${API_BASE_URL.DFD_ITEM}/delete`, {
        params: {
          cnpj: itemToDelete.cnpj,
          dfdNum: itemToDelete.dfdNum,
          id: itemToDelete.id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setShouldFetch(!shouldFetch);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir o item:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  function formatCurrency(value) {
    if (isNaN(value)) return value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  function unformatCurrency(value) {
    return value.replace(/\D/g, '') / 100;
  }

  function calculateTotal(quantidade, vlrUnit) {
    const quantidadeNum = parseFloat(quantidade) || 0;
    const vlrUnitNum = unformatCurrency(vlrUnit) || 0;
    return formatCurrency(quantidadeNum * vlrUnitNum);
  }

  const handleInputChange = (localIndex, field, value) => {
    const globalIndex = startIndex + localIndex;
    // Verifica se o índice global existe antes de tentar atualizar
    if (!dfdItens[globalIndex]) {
      console.error('Tentando acessar um índice inexistente:', globalIndex);
      return;
    }
    const updatedDfdItens = [...dfdItens];
    if (field === 'vlrUnit') {
      updatedDfdItens[globalIndex][field] = formatCurrency(
        unformatCurrency(value)
      );
    } else {
      updatedDfdItens[globalIndex][field] = value;
    }

    setDfdItens(updatedDfdItens);
  };

  const handleSaveAll = () => {
    const itemsToSave = dfdItens.map(item => ({
      ...item,
      vlrUnit: unformatCurrency(item.vlrUnit),
    }));
    axios
      .post(`${API_BASE_URL.DFD_ITEM}/saveAll`, itemsToSave, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        showDefaultModal('success', 'Itens atualizados com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao atualizar os itens:', error);
        showDefaultModal('error', 'Erro ao salvar o registro');
      });
  };

  async function handleDownload() {
    try {
      const response = await axios.post(
        `${API_BASE_URL.DFD}/exportExcel`,
        dfd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob', // Para lidar com o arquivo como binário
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DFD ${formatString(dfd.num, 4)}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }

  const [selectedDfdNum, setSelectedDfdNum] = useState('');

  function handleSelectDfdFromList() {
    const selected = dfdList.find(d => String(d.num) === String(selectedDfdNum));
    if (selected) {
      setDfd(selected);
      setDfdList([]);
    }
  }

  return (
    <div className="full-screen-div">
      <Navbar />
      <div className="container mt-4 pt-5 small">
        <ResponsiveShadow className="container mt-4 mb-4">
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-1 mb-2 border-bottom">
            <h6 className="text-secondary">Itens do DFD</h6>
          </div>
          <div className="d-flex justify-content-start mb-1">
            <Link
              to="/pca"
              state={{ dfd: dfd }}
              className="btn btn-secondary btn-sm me-2 d-inline-flex align-items-center"
            >
              <i className="bi bi-arrow-left me-2" />
              Voltar
            </Link>
            {(dfd.tramitacao === 'EM ELABORAÇÃO' ||
              dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES') && (
              <button
                type="button"
                onClick={() => setShowFindModal(true)}
                className="btn btn-success btn-sm me-2 d-inline-flex align-items-center"
              >
                <i className="bi bi-plus-lg me-2" />
                Adicionar Item
              </button>
            )}
            {(dfd.tramitacao === 'EM ELABORAÇÃO' ||
              dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES') && (
              <button
                onClick={handleSaveAll}
                className="btn btn-primary btn-sm me-2 d-inline-flex align-items-center"
              >
                <i className="bi bi-save me-2" />
                Salvar Todos
              </button>
            )}
            <button
              onClick={handleDownload}
              className="btn btn-primary btn-sm me-2 d-inline-flex align-items-center"
            >
              <i className="bi bi-file-excel me-2" />
              Exportar Planilha Excel
            </button>
            {/* Se não veio `dfd` por estado e há mais de um DFD disponível, permitir seleção */}
            {!dfd.num && dfdList && dfdList.length > 0 && (
              <div className="d-inline-flex align-items-center ms-2">
                <select
                  className="form-select form-select-sm me-2"
                  style={{ minWidth: '220px' }}
                  value={selectedDfdNum}
                  onChange={e => setSelectedDfdNum(e.target.value)}
                >
                  <option value="">Selecione DFD...</option>
                  {dfdList.map(d => (
                    <option key={`${d.cnpj}-${d.num}`} value={d.num}>
                      {`${d.num} - ${d.requisitante} - ${d.tramitacao}`}
                    </option>
                  ))}
                </select>
                <button className="btn btn-success btn-sm" onClick={handleSelectDfdFromList}>
                  Abrir Itens
                </button>
              </div>
            )}
            {(dfd.tramitacao === 'EM ELABORAÇÃO' ||
              dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES') && (
              <button
                className="btn btn-primary btn-sm me-2 d-inline-flex align-items-center"
                onClick={() => setShowExcelUploadModal(true)}
              >
                <i className="bi bi-file-excel me-2" />
                Importar Planilha Excel
              </button>
            )}
          </div>
          <div className="table-responsive">
            <table className="table table-hover table-sm small table-custom-radius">
              <TableHeader columns={[
                { label: 'Grupo', width: '5%', align: 'center' },
                { label: 'Classe', width: '5%', align: 'center' },
                { label: 'Código', width: '5%', align: 'center' },
                { label: 'Descrição', align: 'center' },
                { label: 'Categoria', width: '10%', align: 'center' },
                { label: 'Unidade', width: '10%', align: 'center' },
                { label: 'Quantidade', width: '10%', align: 'center' },
                { label: 'Valor Unitário', width: '10%', align: 'center' },
                { label: 'Valor Total', width: '10%', align: 'center' },
                ...(dfd.tramitacao === 'EM ELABORAÇÃO' ? [{ label: 'Ação', align: 'center' }] : []),
              ]} />
              <tbody>
                {selectedDfdItens.length > 0 ? (
                  selectedDfdItens.map((c, index) => (
                    <tr key={`${c.cnpj}-${c.dfdNum}-${c.id}`}>
                      <td style={{ width: '5%' }}>{c.grupo}</td>
                      <td style={{ width: '5%' }}>{c.classe}</td>
                      <td style={{ width: '5%' }}>{c.item}</td>
                      <td>{c.descricao}</td>
                      <td className="text-end">
                        {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                        dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                          <select
                            name="categoria"
                            value={c.categoria}
                            onChange={e =>
                              handleInputChange(
                                index,
                                'categoria',
                                e.target.value
                              )
                            }
                            className="form-select form-select-sm"
                            style={{ minWidth: '100px' }}
                          >
                            <option value="">Selecione...</option>
                            {categoriaItem.map(categoria => (
                              <option
                                key={categoria.id}
                                value={categoria.descricao}
                              >
                                {categoria.descricao}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>{c.categoria}</span>
                        )}
                      </td>
                      <td className="text-end">
                        {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                        dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                          <select
                            name="unidade"
                            value={c.unidade}
                            onChange={e =>
                              handleInputChange(
                                index,
                                'unidade',
                                e.target.value
                              )
                            }
                            className="form-select form-select-sm"
                            style={{ minWidth: '100px' }}
                          >
                            <option value="">Selecione...</option>
                            {unidades.map(unidade => (
                              <option
                                key={unidade.id}
                                value={unidade.descricao}
                              >
                                {unidade.descricao}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>{c.unidade}</span>
                        )}
                      </td>
                      <td className="text-end">
                        {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                        dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                          <input
                            type="number"
                            name="quantidade"
                            value={c.quantidade ?? ''}
                            onChange={e =>
                              handleInputChange(
                                index,
                                'quantidade',
                                e.target.value
                              )
                            }
                            className="form-control form-control-sm text-end"
                            style={{ minWidth: '100px' }}
                            min="0"
                          />
                        ) : (
                          <span>{formatQuantidade(c.quantidade)}</span>
                        )}
                      </td>
                      <td className="text-end">
                        {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                        dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                          <input
                            type="text"
                            name="vlrUnit"
                            value={c.vlrUnit ?? ''}
                            onChange={e =>
                              handleInputChange(
                                index,
                                'vlrUnit',
                                e.target.value
                              )
                            }
                            className="form-control form-control-sm text-end"
                            style={{ minWidth: '100px' }}
                          />
                        ) : (
                          <span>{c.vlrUnit ?? ''}</span>
                        )}
                      </td>
                      <td className="text-end">
                        {dfd.tramitacao === 'EM ELABORAÇÃO' ||
                        dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES' ? (
                          <input
                            disabled
                            type="text"
                            name="vlrTotal"
                            value={calculateTotal(c.quantidade, c.vlrUnit)}
                            className="form-control form-control-sm text-end"
                            style={{ minWidth: '100px' }}
                          />
                        ) : (
                          <span>{calculateTotal(c.quantidade, c.vlrUnit)}</span>
                        )}
                      </td>
                      {(dfd.tramitacao === 'EM ELABORAÇÃO' ||
                        dfd.tramitacao === 'DEVOLVIDO PARA AJUSTES') && (
                        <td>
                          <button
                            onClick={() => handleDelete(c)}
                            className="btn btn-sm"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Excluir item"
                          >
                            <i className="bi bi-trash3 text-danger" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      Nenhum registro encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination {...setPagination} />
          </div>
        </ResponsiveShadow>
      </div>
      {/* Modal para upload */}
      <ExcelUploadModal
        show={showExcelUploadModal}
        onClose={() => setShowExcelUploadModal(false)}
        onSuccess={() => setShouldFetch(!shouldFetch)}
        dfd={dfd}
      />
      <DfdFindItemModal
        isOpen={showFindModal}
        onClose={() => setShowFindModal(false)}
        dfd={{ ...dfd, tipo: dfd.tipo || location.state?.dfd?.tipo || '' }}
        onAdded={() => {
          setShowFindModal(false);
          setShouldFetch(prev => !prev);
        }}
      />
      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <Footer />
    </div>
  );
}

export default PcaItens;
